import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { inputCls } from "../../components/forms/FormBits";
import { can } from "../../lib/auth";
import IntegrationActivity from "./IntegrationActivity";

const STATUS = {
  pending: ["Pending", "bg-amber-100 text-amber-800"],
  approved: ["Approved", "bg-emerald-100 text-emerald-800"],
  rejected: ["Rejected", "bg-red-100 text-red-800"],
  information_requested: ["Information Requested", "bg-sky-100 text-sky-800"],
  suspended: ["Suspended", "bg-slate-200 text-slate-800"],
};
const ENTITY = { limited_company: "Limited company", sole_recruiter: "Sole recruiter", partnership: "Partnership" };
const MOTIVE = { more_roles: "More roles to work on", candidates: "Candidates for my roles", both: "Both", looking: "Having a look" };
const NICHES = ["Healthcare", "IT & Technology", "Engineering", "Finance & Accounting", "Construction", "Other"];

export const Badge = ({ status }) => {
  const [label, cls] = STATUS[status] || [status, "bg-slate-100 text-slate-700"];
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid={`pc-status-${status}`}>{label}</span>;
};

const Row = ({ label, value }) => (
  <div className="grid grid-cols-[160px_1fr] gap-3 border-b border-border/60 py-2 text-sm"><span className="text-muted-foreground">{label}</span><span className="text-charcoal">{value || "—"}</span></div>
);

export default function MembershipQueue({ currentUser }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", country: "", entity_type: "", niche: "" });
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  const canApprove = can(currentUser, "partnerships", "approve");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const { data } = await api.get(`/pc/admin/applications${qs ? `?${qs}` : ""}`);
      setRows(data);
    } catch { toast.error("Failed to load applications"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    const { data } = await api.get(`/pc/admin/applications/${id}`);
    setDetail(data); setNote(""); setIssued(null);
  };

  const act = async (action) => {
    if (["reject", "request-info"].includes(action) && note.trim().length < 3) { toast.error("A note is required for this action."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/pc/admin/applications/${detail.id}/${action}`, { note });
      toast.success(`Application ${data.status.replace("_", " ")}`);
      if (data.temp_password) setIssued({ email: data.login_email, password: data.temp_password });
      const fresh = await api.get(`/pc/admin/applications/${detail.id}`);
      setDetail(fresh.data); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const countries = [...new Set(rows.map((r) => r.country))].sort();

  return (
    <div data-testid="membership-queue">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div><h2 className="font-serif text-2xl text-navy">Partnership Applications</h2><p className="text-sm text-muted-foreground">Recruitment Partnership Center — membership queue</p></div>
        <div className="flex flex-wrap gap-2">
          <select className={inputCls + " w-auto"} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="pc-filter-status"><option value="">All statuses</option>{Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}</select>
          <select className={inputCls + " w-auto"} value={filters.entity_type} onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })} data-testid="pc-filter-entity"><option value="">All types</option>{Object.entries(ENTITY).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select className={inputCls + " w-auto"} value={filters.niche} onChange={(e) => setFilters({ ...filters, niche: e.target.value })} data-testid="pc-filter-niche"><option value="">All niches</option>{NICHES.map((n) => <option key={n}>{n}</option>)}</select>
          <input className={inputCls + " w-40"} placeholder="Country" list="pc-countries" value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} data-testid="pc-filter-country" />
          <datalist id="pc-countries">{countries.map((c) => <option key={c} value={c} />)}</datalist>
        </div>
      </div>

      {loading ? <Loader2 className="h-5 w-5 animate-spin text-navy" /> : rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="pc-queue-empty">No applications match these filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-xs uppercase tracking-wide text-muted-foreground"><tr>{["Trading name", "Type", "Country", "Coverage", "Niches", "Motivation", "Submitted", "Status"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => open(r.id)} className="cursor-pointer border-t border-border/60 hover:bg-secondary/60" data-testid={`pc-app-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-navy">{r.trading_name}</td>
                  <td className="px-4 py-3">{ENTITY[r.entity_type]}</td>
                  <td className="px-4 py-3">{r.country}</td>
                  <td className="px-4 py-3">{r.coverage}</td>
                  <td className="px-4 py-3">{r.recruitment_niches.join(", ")}</td>
                  <td className="px-4 py-3">{MOTIVE[r.motivation]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{new Date(r.submitted_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><Badge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="pc-app-detail">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-3 font-serif text-2xl text-navy">{detail.trading_name} <Badge status={detail.status} /></DialogTitle></DialogHeader>
              <div className="mt-2">
                <Row label="Applicant" value={`${detail.first_name} ${detail.last_name}`} />
                <Row label="Business email" value={detail.business_email} />
                <Row label="Phone" value={detail.phone} />
                <Row label="Business type" value={ENTITY[detail.entity_type]} />
                <Row label="Registration no." value={detail.company_registration_number} />
                <Row label="Website" value={detail.website} />
                <Row label="Country" value={detail.country} />
                <Row label="Coverage" value={detail.coverage} />
                <Row label="Niches" value={detail.recruitment_niches.join(", ")} />
                <Row label="Motivation" value={MOTIVE[detail.motivation]} />
                <Row label="Submitted" value={new Date(detail.submitted_at).toLocaleString()} />
                <Row label="Reviewed" value={detail.reviewed_at ? `${new Date(detail.reviewed_at).toLocaleString()} · ${detail.reviewed_by}` : ""} />
                <Row label="Reviewer notes" value={detail.reviewer_notes} />
                <Row label="Partner login" value={detail.partner_user ? `${detail.partner_user.email} · ${detail.partner_user.status}` : "Not created"} />
              </div>
              {issued && (
                <div className="mt-4 rounded-xl border border-gold/40 bg-gold/5 p-4 text-sm" data-testid="pc-temp-credentials">
                  <p className="font-semibold text-navy">Partner Dashboard access issued — share securely with the applicant (shown once):</p>
                  <p className="mt-2 font-mono text-xs">Email: {issued.email}</p>
                  <p className="flex items-center gap-2 font-mono text-xs">Temporary password: <span data-testid="pc-temp-password">{issued.password}</span>
                    <button type="button" onClick={() => navigator.clipboard?.writeText(issued.password)} className="text-gold" aria-label="Copy"><Copy className="h-3.5 w-3.5" /></button></p>
                  <p className="mt-1 text-xs text-muted-foreground">They sign in at /partner-dashboard and must set a new password on first login.</p>
                </div>
              )}
              {canApprove && (
                <div className="mt-5 rounded-xl border border-border bg-secondary/50 p-4" data-testid="pc-review-panel">
                  <p className="text-sm font-semibold text-navy">Review action</p>
                  <textarea rows={2} className={inputCls + " mt-2"} placeholder="Note / reason (required for Reject and Request Information)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="pc-review-note" />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {["pending", "information_requested", "suspended", "rejected"].includes(detail.status) && <button disabled={busy} onClick={() => act("approve")} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="pc-action-approve">Approve</button>}
                    {detail.status !== "rejected" && <button disabled={busy} onClick={() => act("reject")} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60" data-testid="pc-action-reject">Reject</button>}
                    {["pending", "approved"].includes(detail.status) && <button disabled={busy} onClick={() => act("request-info")} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60" data-testid="pc-action-request-info">Request Information</button>}
                    {detail.status !== "suspended" && <button disabled={busy} onClick={() => act("suspend")} className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60" data-testid="pc-action-suspend">Suspend</button>}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <IntegrationActivity />
    </div>
  );
}
